import { wallOpeningOpening2, wallOpeningSide } from "building-common/openings/openingQuery";
import type { Building } from "building-types/building/buildingTypes";
import { WallVerticalDatumAnchor } from "building-types/datums/datumsTypesEnum";
import { DatumVerticalUsageType } from "building-types/datums/datumUsageTypes";
import type { SideWallSweeps, WallSweepRail, WallSweeps, WallSweepSystem } from "building-types/facades/facadeTypes";
import type { MaterialWallSweep, WallSweep, WallSweepInfo } from "building-types/facades/wallSweep";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import { roomEntityHandleForSelectingReportingUnit } from "building-types/handles/roomEntityHandleForSelectingReportingUnit";
import type { Level } from "building-types/levels/levelTypes";
import type { OpeningSideName } from "building-types/openings/openingTypes";
import {
  DEFAULT_PROFILE_DESCRIPTOR,
  DEFAULT_TRIM_PROFILE_DESCRIPTOR,
} from "building-types/profiles/defaultBoardTrimProfiles";
import type { RoofWallSystemEdge } from "building-types/roofs/roofWallSystemTypes";
import type { Room } from "building-types/rooms/roomTypes";
import type { BuildingGenerationIssue } from "building-types/settings/buildingGenerationIssue";
import type { ActiveProfileSource } from "building-types/settings/profileCatalogSettings";
import type { BaseDescriptor } from "building-types/views/baseDescriptorTypes";
import type { FaceArrayViewEntity } from "building-types/views/viewEntity";
import type { BRepViewEntity, ExtrusionViewEntity } from "building-types/views/viewEntityTypes";
import type { WallSideDescriptor } from "building-types/walls/wallDesciptorTypes";
import { WallCladdingType, WallSideCutType } from "building-types/walls/wallDesciptorTypes";
import { WallType } from "building-types/walls/wallEnums";
import { WallLayerType } from "building-types/walls/wallEnums";
import type { WallGap } from "building-types/walls/wallGap";
import type { WallIndex } from "building-types/walls/wallIndex";
import type { WallSystem } from "building-types/walls/wallSytemTypes";
import type { Wall } from "building-types/walls/wallTypes";
import { WallLOD } from "building-types/walls/wallTypes";
import type { RWTable, Table } from "common/data/table";
import type { LevelInches, WorldInches } from "common/types/nominalNumbersTypes";
import { assertIsNotNullOrUndefined } from "common/utils/assertOfType";
import { assertUnreachable } from "common/utils/assertUnreachable";
import { worldInchesNew, worldInchesTolevel } from "common/utils/nominalNumbers";
import { resolveInstanceScopedEntityId } from "doc/entity/componentInstanceEntityResolve";
import { exteriorMaterialGeometryNew } from "doc/utils/materialTools";
import { MULTIGABLE_BSP_EPSILON } from "doc/utils/roofTools";
import { brepViewEntityNew } from "doc/utils/viewEntityTools";
import type { Face } from "geom/brep";
import { faceNew, faceRepairLoopDirections } from "geom/brep";
import { addConvexPlanesToBSPLeaf } from "geom/bsp";
import type { ClipFaceContext } from "geom/bspClipping";
import { clipFaces, clippingNodeNew, clipRail3, clipRailOnPlane } from "geom/bspClipping";
import type { NodeBSP } from "geom/bspTypes";
import { closestParamLine2 } from "geom/closestPoint";
import { ANGLE_EPSILON, ONE_MINUS_DIST_EPSILON, WindingDir } from "geom/core";
import { extrudeFaceAlongPolyLine3, extrusionToBRep } from "geom/extrude";
import { line2Collinear } from "geom/line2";
import { polyLine2Thicken } from "geom/offset";
import { planeCopy, planeMove, planeNormal, planeReProjectPolyLine2, polyLine2OnPlane } from "geom/plane";
import { planeBSPByPointAndNormal, planeBSPNew } from "geom/planeBSP";
import type { Plane } from "geom/planeTypes";
import { polygonUnion } from "geom/polyclip/polyClip";
import { polygonSubtract } from "geom/polygon";
import {
  polyLine2ByTwoPoints,
  polyLine2Copy,
  polyLine2Edge,
  polyLine2EdgeCount,
  polyLine2EdgeEval,
  polyLine2EdgeLength,
  polyLine2EdgeTangent,
  polyLine2End,
  polyLine2Extend,
  polyLine2Length,
  polyLine2MergeCoincidentVertices,
  polyLine2Reverse,
  polyLine2ReverseCopy,
  polyLine2SetCW,
  polyLine2Simplify,
  polyLine2Start,
  polyLine2WindingDir,
} from "geom/polyLine2";
import { polyLine2New, type PolyLine2 } from "geom/polyLine2Types";
import { polyLine3Copy, polyLine3Move, polyLine3OnPlane } from "geom/polyLine3";
import { rect2ByBottomLeftWidthHeight } from "geom/rect2";
import { vec2Dot } from "geom/vec2";
import { vec3AngleBetween } from "geom/vec3";
import { isCombinedGlobalInstanceMirror } from "../generationTools/generateOpeningsTools";
import {
  applyExtensionsToFacadeSweepRail2,
  facadeSweepRail2FromFaceBounds,
  facadeSweepRail2FromRayInFace,
  wallSweepRailNew,
  wallSweepsNew,
} from "../utils/facade";
import { sideOpposite } from "./openings/sideOpposite";
import { pushMissingProfileReferenceIssue } from "./profileMissingReference";
import {
  getActiveProfileDescriptor,
  getProfileExtrusionDescriptorProfile,
  overrideDescriptorInfo,
} from "./profileTools";
import { getEaveDepth, getSoffitDepth } from "./roofTrimUtility";
import { getWallGaps } from "./walls/solidWallCrossSectionUtils";
import { getHostedRoomForWall, getWallPath, wallSideHasCladdingType } from "./walls/wall";
import {
  solidWallLayerWithFallback,
  wallCrossSectionLayer,
  wallCrossSectionLayers,
  wallMassLayers,
} from "./walls/wallLayers";
import {
  wallRoomCanApplyFacade,
  wallSectionSideCanApplyFacade,
  wallSectionSideCutType,
} from "./walls/wallSectionTools";
import { isSolidWallMass, wallIsSolid } from "./walls/wallTypeGuards";

interface MakeWallSweepsOptions {
  readonly levels: Table<Level>;
  readonly facades: Building["facades"];
  readonly walls: Building["walls"];
  readonly roofs: Building["roofSystems"];
  readonly profileSource: ActiveProfileSource;
  readonly mirror: boolean;
  readonly issues?: BuildingGenerationIssue[];
}

export function makeWallSweeps({
  levels,
  facades,
  walls,
  roofs,
  profileSource,
  mirror,
  issues,
}: MakeWallSweepsOptions) {
  const wallSweeps: { [levelId: string]: WallSweepSystem } = {};
  for (const levelId in levels) {
    const level = levels[levelId];
    const levelWalls = walls[levelId];

    const levelWallSweeps = makeLevelWallSweeps({
      level,
      facades,
      wallSystem: levelWalls,
      profileSource,
      isRoofWall: false,
      mirror,
      issues,
    });

    wallSweeps[levelId] = { sweeps: levelWallSweeps };
  }

  for (const roofId in roofs) {
    const roof = roofs[roofId];

    const { roofs: subRoofs } = roof;
    for (const subRoofId in subRoofs) {
      const { roofVolume, roofWallSystem, roofSettings, legacyRoof, suppressWallExtension } = subRoofs[subRoofId];

      if (!roofWallSystem) {
        continue;
      }

      const { discardWallsBSP, baseHeight, ceilingHeight, heelHeightOffsets } = roofVolume;

      const roofOffsetZ = baseHeight - ceilingHeight;
      const roofSoffitDepth = getSoffitDepth(roofSettings);

      const basePlaneHeight = legacyRoof
        ? baseHeight +
          (suppressWallExtension
            ? Math.min(heelHeightOffsets.refOffset, 0) - getEaveDepth(roofSettings)
            : -Math.max(heelHeightOffsets.refOffset, 0))
        : baseHeight - heelHeightOffsets.refOffset;
      const planeBSP = planeBSPNew(0, 0, 1, basePlaneHeight);

      const sweepFacesClippingBSP = addConvexPlanesToBSPLeaf([planeBSP]);

      let roofWallSweeps = makeLevelWallSweeps({
        level: roofWallSystem.levels[roofWallSystem.levelId],
        facades,
        wallSystem: roofWallSystem.wallSystem,
        profileSource,
        isRoofWall: true,
        legacyRoof,
        roofEdges: roofWallSystem.edges,
        roofOffsetZ,
        roofFacadeTrimOffsetZ: roofWallSystem.facadeTrimOffsetZ,
        roofSoffitDepth,
        sweepFacesClippingBSP,
        discardWallsBSP: legacyRoof ? discardWallsBSP : null,
        mirror,
        issues,
      });

      const removeSweepsAboveRoof: ClipFaceContext = {
        clippingNodes: [clippingNodeNew(discardWallsBSP, true)],
        epsilon: MULTIGABLE_BSP_EPSILON,
      };

      roofWallSweeps = clipWallSweeps(roofWallSweeps, removeSweepsAboveRoof);

      wallSweeps[roofWallSystem.levelId] = { sweeps: roofWallSweeps };
    }
  }

  return wallSweeps;
}

function clipSideWallSweeps(wallSweeps: SideWallSweeps, options: ClipFaceContext): SideWallSweeps {
  const { geometry, rails } = wallSweeps;
  const { materialBreps, materialExtrusions, materialFaces } = geometry;

  const clippedMaterialFaces: FaceArrayViewEntity[] = [];
  const clippedWallSweepRails: WallSweepRail[] = [];

  const clipRail = ({ rail, profileName, profileId, material }: WallSweepRail) => {
    const clippedRails = clipRail3({
      rail,
      options,
    });

    clippedWallSweepRails.push(...clippedRails.map((rail) => ({ rail, profileName, profileId, material })));
  };

  const clipBrep = ({ brep, entityId, descriptor, material, materialTransformation, pickHandle }: BRepViewEntity) => {
    const clippedFaces = clipFaces({
      faces: brep.faces,
      options,
    });

    clippedMaterialFaces.push({
      faces: clippedFaces,
      entityId,
      descriptor,
      material,
      materialTransformation,
      pickHandle,
    });
  };

  const clipExtrusion = ({
    extrusion,
    entityId,
    descriptor,
    material,
    materialTransformation,
    pickHandle,
  }: ExtrusionViewEntity) => {
    const brep = extrusionToBRep(extrusion);

    const clippedFaces = clipFaces({
      faces: brep.faces,
      options,
    });

    clippedMaterialFaces.push({
      faces: clippedFaces,
      entityId,
      descriptor,
      material,
      materialTransformation,
      pickHandle,
    });
  };

  const clipFaceGroup = ({
    faces,
    entityId,
    descriptor,
    material,
    materialTransformation,
    pickHandle,
  }: FaceArrayViewEntity) => {
    const clippedFaces = clipFaces({
      faces,
      options,
    });

    clippedMaterialFaces.push({
      faces: clippedFaces,
      entityId,
      descriptor,
      material,
      materialTransformation,
      pickHandle,
    });
  };

  for (const materialBrep of materialBreps) {
    clipBrep(materialBrep);
  }

  for (const materialExtrusion of materialExtrusions) {
    clipExtrusion(materialExtrusion);
  }

  for (const faceGroup of materialFaces) {
    clipFaceGroup(faceGroup);
  }

  for (const rail of rails) {
    clipRail(rail);
  }

  return {
    rails: clippedWallSweepRails,
    geometry: {
      materialBreps: [],
      materialExtrusions: [],
      materialFaces: clippedMaterialFaces,
    },
  };
}

function clipWallSweeps(wallSweeps: Table<WallSweeps>, options: ClipFaceContext): Table<WallSweeps> {
  const clippedWallSweeps: RWTable<WallSweeps> = {};
  for (const wallId in wallSweeps) {
    const { leftWallSweeps, rightWallSweeps } = wallSweeps[wallId];

    const newLeftWallSweeps = clipSideWallSweeps(leftWallSweeps, options);
    const newRightWallSweeps = clipSideWallSweeps(rightWallSweeps, options);

    clippedWallSweeps[wallId] = {
      leftWallSweeps: newLeftWallSweeps,
      rightWallSweeps: newRightWallSweeps,
    };
  }

  return clippedWallSweeps;
}

interface MakeLevelWallTrimOptions {
  readonly level: Level;
  readonly wallSystem: WallSystem;
  readonly facades: Building["facades"];
  readonly profileSource: ActiveProfileSource;
  readonly isRoofWall: boolean;
  readonly mirror: boolean;
  readonly legacyRoof?: boolean;
  readonly sweepFacesClippingBSP?: NodeBSP | null;
  readonly discardWallsBSP?: NodeBSP | null;
  readonly roofOffsetZ?: number;
  readonly roofFacadeTrimOffsetZ?: number;
  readonly roofSoffitDepth?: number;
  readonly roofEdges?: Table<RoofWallSystemEdge>;
  readonly issues?: BuildingGenerationIssue[];
}

function makeLevelWallSweeps({
  level,
  wallSystem,
  facades,
  profileSource,
  isRoofWall,
  sweepFacesClippingBSP,
  discardWallsBSP,
  legacyRoof = false,
  roofOffsetZ = 0,
  roofFacadeTrimOffsetZ = 0,
  roofSoffitDepth = 0,
  roofEdges = {},
  mirror,
  issues,
}: MakeLevelWallTrimOptions): { [wallId: string]: WallSweeps } {
  const levelWallSweeps: { [wallId: string]: WallSweeps } = {};

  const wallIndex = wallSystem.wallIndex;

  for (const wallId in wallSystem.walls) {
    const wall = wallSystem.walls[wallId];

    const leftSweepGeometry = exteriorMaterialGeometryNew();
    const leftSweepRails: WallSweepRail[] = [];
    const leftWallSweepInfos: WallSweepInfo[] = [];

    const rightSweepGeometry = exteriorMaterialGeometryNew();
    const rightSweepRails: WallSweepRail[] = [];
    const rightWallSweepInfos: WallSweepInfo[] = [];

    const leftFacadeWallSweeps = makeFacadeWallSweeps(wall, facades, true, isRoofWall);
    const rightFacadeWallSweeps = makeFacadeWallSweeps(wall, facades, false, isRoofWall);

    // add facade-associated wallSweeps with highest precedence (lowest number)
    if (leftFacadeWallSweeps.length) {
      leftWallSweepInfos.push(...leftFacadeWallSweeps);

      applyWallSweepsToWallSide({
        level,
        wallIndex,
        wall,
        isLeftSide: true,
        wallSweepInfos: leftWallSweepInfos,
        breps: leftSweepGeometry.materialBreps,
        rails: leftSweepRails,
        profileSource,
        isRoofWall,
        sweepFacesClippingBSP,
        legacyRoof,
        discardWallsBSP,
        roofOffsetZ,
        roofFacadeTrimOffsetZ,
        roofSoffitDepth,
        roofEdges,
        mirror,
        issues,
      });
    }

    if (rightFacadeWallSweeps.length) {
      rightWallSweepInfos.push(...rightFacadeWallSweeps);

      applyWallSweepsToWallSide({
        level,
        wallIndex,
        wall,
        isLeftSide: false,
        wallSweepInfos: rightWallSweepInfos,
        breps: rightSweepGeometry.materialBreps,
        rails: rightSweepRails,
        profileSource,
        isRoofWall,
        sweepFacesClippingBSP,
        legacyRoof,
        discardWallsBSP,
        roofOffsetZ,
        roofFacadeTrimOffsetZ,
        roofSoffitDepth,
        roofEdges,
        mirror,
        issues,
      });
    }

    if (!levelWallSweeps[wallId]) {
      levelWallSweeps[wallId] = wallSweepsNew();
    }

    const { leftWallSweeps, rightWallSweeps } = levelWallSweeps[wallId];

    leftWallSweeps.rails.push(...leftSweepRails);
    leftWallSweeps.geometry.materialBreps.push(...leftSweepGeometry.materialBreps);
    leftWallSweeps.geometry.materialExtrusions.push(...leftSweepGeometry.materialExtrusions);

    rightWallSweeps.rails.push(...rightSweepRails);
    rightWallSweeps.geometry.materialBreps.push(...rightSweepGeometry.materialBreps);
    rightWallSweeps.geometry.materialExtrusions.push(...rightSweepGeometry.materialExtrusions);
  }

  return levelWallSweeps;
}

function makeFacadeWallSweeps(wall: Wall, facades: Building["facades"], isLeftSide: boolean, isRoofWall: boolean) {
  const path = getWallPath(wall);
  const { wallDescriptor, leftRoom, rightRoom } = path;
  const { leftSide, rightSide } = wallDescriptor;

  const facadeWallSweeps: WallSweepInfo[] = [];

  if (!wallSideCanApplyFacade(wall, isLeftSide)) {
    return facadeWallSweeps;
  }

  const instanceId = leftRoom?.instanceId ?? rightRoom?.instanceId;
  const requiredAnchor = isRoofWall ? WallVerticalDatumAnchor.GABLE_END : WallVerticalDatumAnchor.BASE_WALL;

  const makeWallSideSweeps = (wallSide: WallSideDescriptor) => {
    const wallSideSweeps: MaterialWallSweep[] = [];
    for (const stratum of wallSide.wallFacadeSweeps) {
      const { parentId: facadeId, datumUsage, sweep, material, materialTransformation } = stratum;
      const { offset: datumOffset = 0 } = datumUsage;
      const datumId = resolveInstanceScopedEntityId({ path: datumUsage, table: wallSide.wallFacadeDatums });
      if (!datumId) {
        continue;
      }

      const datum = wallSide.wallFacadeDatums[datumId];
      if (!datum || !facadeId) {
        continue;
      }

      const legacyRoofBehavior = facades[facadeId]?.legacyRoofBehavior ?? false;

      // A facade sweep inside a component instance is selected as the instance.
      const facadePickHandle: DocEntityHandle = instanceId
        ? { type: "COMPONENT_INSTANCE", id: instanceId }
        : { type: "FACADE", id: facadeId };

      //combine properties from wallSweep and Facade
      if (datum.isValidForUsage(DatumVerticalUsageType.WALL_VERTICAL) && datum.hasCompatibleAnchor(requiredAnchor)) {
        const wallSweep: WallSweep = { ...sweep, datum, datumOffset };

        wallSideSweeps.push({
          facadePickHandle,
          wallSweep,
          material: material ?? "trimExterior0",
          materialTransformation: materialTransformation
            ? {
                ...materialTransformation,
                uTranslation: materialTransformation?.uTranslation ?? 0,
                vTranslation: materialTransformation?.vTranslation ?? 0,
                rotationDegrees: materialTransformation?.rotationDegrees ?? 0,
              }
            : undefined,
          legacyRoofBehavior,
        });
      }
    }
    return wallSideSweeps;
  };

  const wallSide = isLeftSide ? leftSide : rightSide;
  const wallOppSide = isLeftSide ? rightSide : leftSide;

  const matWallSweeps: MaterialWallSweep[] = [];

  /**
   * @NOTE - we are currently duplicating sweeps on a single side. This behavior is legacy, and should
   * be removed in a dedicated PR. Ideally we would filter the sweeps by id to make sure only one instance
   * of a sweep is ever applied to a wall side.
   */
  matWallSweeps.push(...makeWallSideSweeps(wallOppSide));
  matWallSweeps.push(...makeWallSideSweeps(wallSide));

  if (matWallSweeps.length > 0) {
    facadeWallSweeps.push({ wallSweeps: matWallSweeps, outside: true });
  }

  return facadeWallSweeps;
}

/**
 * This class exists to de-duplicate the clipping of wall faces repeatedly
 * by multiple facade sweeps on a wall.
 *
 * It stores it's own state because we don't want to eagerly clip walls
 * unless absolutely necessary, so rather than pre-clipping
 * sections of walls if they are unused by trims, this will clip them only if
 * specified and only once per id specified by the caller
 */
class WallFaceClipProvider {
  clipFaces(faces: Face[], id: string) {
    if (!this.clippedFaces[id]) {
      this.clippedFaces[id] = this.clipFacesInternal(faces);
    }
    return this.clippedFaces[id];
  }

  constructor(clippingNode?: NodeBSP | null) {
    if (clippingNode) {
      this.clipContext = {
        clippingNodes: [clippingNodeNew(clippingNode)],
        epsilon: MULTIGABLE_BSP_EPSILON,
      };
    }
  }

  private clippedFaces: { [id: string]: Face[] } = {};
  private clipContext: ClipFaceContext | null = null;
  private clipFacesInternal(faces: Face[]) {
    if (this.clipContext) {
      return clipFaces({
        faces,
        options: this.clipContext,
      });
    }
    return faces;
  }
}

interface ApplyWallSweepsToWallSideOptions<T extends Wall> {
  readonly wallSweepInfos: WallSweepInfo[];
  readonly wall: T;
  readonly breps: BRepViewEntity[];
  readonly rails: WallSweepRail[];
  readonly profileSource: ActiveProfileSource;
  readonly mirror: boolean;
  readonly level: Level;
  readonly wallIndex: WallIndex;
  readonly isLeftSide: boolean;
  readonly isRoofWall: boolean;
  readonly sweepFacesClippingBSP?: NodeBSP | null;
  readonly legacyRoof?: boolean;
  readonly discardWallsBSP?: NodeBSP | null;
  readonly roofOffsetZ?: number;
  readonly roofFacadeTrimOffsetZ?: number;
  readonly roofSoffitDepth?: number;
  readonly roofEdges?: Table<RoofWallSystemEdge>;
  readonly issues?: BuildingGenerationIssue[];
}

function applyWallSweepsToWallSide({
  wallSweepInfos,
  wall,
  breps,
  rails,
  profileSource,
  mirror,
  level,
  wallIndex,
  isLeftSide,
  isRoofWall,
  sweepFacesClippingBSP = null,
  legacyRoof = false,
  discardWallsBSP = null,
  roofOffsetZ = 0,
  roofFacadeTrimOffsetZ = 0,
  roofSoffitDepth = 0,
  roofEdges = {},
  issues,
}: ApplyWallSweepsToWallSideOptions<Wall>) {
  if (wallSweepInfos.length === 0) {
    return;
  }

  const path = getWallPath(wall);
  const { wallDescriptor } = path;
  const sweepSide = isLeftSide ? wallDescriptor.leftSide : wallDescriptor.rightSide;
  const effectiveMirror = isCombinedGlobalInstanceMirror(mirror, getHostedRoomForWall(wall, isLeftSide));
  const wallLayer = WallLayerType.SUBSTRATE;
  const wallLOD = WallLOD.L2;
  let wallSweepPerimeterFaces = wallSideFacadePerimeterFaces(
    wall,
    wallLayer,
    wallLOD,
    isLeftSide,
    isRoofWall,
    roofEdges,
  );

  if (!wallSweepPerimeterFaces.length) {
    return;
  }

  const wallClipFaceProvider = new WallFaceClipProvider(sweepFacesClippingBSP);
  const wallStripEdge = facadeWallSweepBaseline(wallIndex, wall, wallLayer, wallLOD, isLeftSide);

  const { startX, startY, endX, endY } = path;
  const wallStripEdgeForVoidWalls = polyLine2ByTwoPoints(startX, startY, endX, endY);

  if (legacyRoof) {
    wallSweepPerimeterFaces = legacyWallSweepPerimeterFaces(wallSweepPerimeterFaces, roofEdges[wall.id]);
  }

  if (!wallSweepPerimeterFaces.length) {
    return;
  }

  for (const { wallSweeps } of wallSweepInfos) {
    for (const sweep of wallSweeps) {
      const { wallSweep } = sweep;
      const { rotation = 0, datum, datumOffset } = wallSweep;
      const legacyRoofBehavior = (!!sweep.legacyRoofBehavior || legacyRoof) && isRoofWall;

      /**
       * @TODO - roof information should not be passed in here.
       * Instead we should mutate the facade sweep before passing in this information
       * in order to keep the wall sweep code agnostic of the type of wall this is
       */
      const wallStripDomain = wallSweep.wallStripDomain || isRoofWall;
      const oppositeSide = isLeftSide ? wallDescriptor.rightSide : wallDescriptor.leftSide;
      const sweepWallGaps = makeWallSweepFacadeWallGaps(wall, wallSweep, wallLayer, isLeftSide);

      const entityHandleForSelectingReportingUnit =
        roomEntityHandleForSelectingReportingUnit(sweepSide.wallRoom.room) ??
        roomEntityHandleForSelectingReportingUnit(oppositeSide.wallRoom.room);

      switch (wall.wallType) {
        case WallType.SOLID_MASS:
        case WallType.VOID: {
          const bottomOffset = isRoofWall && legacyRoofBehavior ? roofOffsetZ : 0;

          let sweepCenterOfRotation: [WorldInches, WorldInches, WorldInches] | null = null;
          const sweepZ = datum.resolveVerticalDatum({
            level,
            extraOffset: datumOffset + bottomOffset,
            wallSide: sweepSide,
          });
          const sweepLevelOffset = worldInchesTolevel(sweepZ, level.floorElevation);

          if (legacyRoofBehavior) {
            sweepCenterOfRotation = makeWallSweepCenterOfRotation(
              wallSweep,
              wallStripEdgeForVoidWalls,
              wallStripEdgeForVoidWalls,
              wallStripDomain,
              sweepZ,
              effectiveMirror,
            );
          } else {
            /**
             * SOLID_MASS and VOID walls use the cross section LOD2 layer for calculating their center of rotation.
             * @TODO - look into a unified positioning method for both solid and VOID walls that does not use
             * LOD1
             */
            const sweepWallCrossSections = wallCrossSectionLayers(wall, wallLayer, wallLOD, sweepLevelOffset);
            const sweepWallCrossSection = sweepWallCrossSections[0];
            if (!sweepWallCrossSection || sweepWallCrossSection.wallType === WallType.VOID) {
              // not an applicable solid mass at this cutPlane.
              // cannot apply a sweep to this location
              continue;
            }

            const wallMassEdge = isLeftSide ? sweepWallCrossSection.leftEdge : sweepWallCrossSection.rightEdge;

            sweepCenterOfRotation = makeWallSweepCenterOfRotation(
              wallSweep,
              wallMassEdge,
              wallStripEdge,
              wallStripDomain,
              sweepZ,
              effectiveMirror,
            );
          }

          /**
           * @TODO - how many cases actually need to filter this. There is a chance this is just a
           * by product of the confusing nature of the sweepCenterOfRoration calculation
           */
          if (sweepCenterOfRotation === null) {
            continue;
          }

          const [sx, sy, sz] = sweepCenterOfRotation;

          const sweepFaces = wallSweepPerimeterFaces;
          const isHorizontalFacadeSweep = Math.abs(rotation) < ANGLE_EPSILON;
          if (!isHorizontalFacadeSweep) {
            breps.push(
              ...makeWallSweepFromRayInFaces(
                sweep,
                sweepFaces,
                sweepWallGaps,
                rails,
                sx,
                sy,
                sz,
                profileSource,
                discardWallsBSP,
                roofSoffitDepth,
                legacyRoofBehavior,
                effectiveMirror,
                entityHandleForSelectingReportingUnit,
                issues,
              ),
            );
          } else if (
            /**
             * This check is necessary to maintain legacy horizontal facade sweep behavior.
             * Ideally every facade would run through the above makeWallSweepFromRayInFaces
             */
            wallSectionSideCanApplyHorizontalFacadeSweep(wall, wallLayer, wallLOD, sweepLevelOffset, isLeftSide)
          ) {
            /**
             * @NOTE - here we position faces redardless of whether they actually cross
             * the bounds of the sweep faces created earlier. Instead, we use the domain of those faces
             * and draw the sweep as if it crossed them, then move the sweep to the desired height
             */
            breps.push(
              ...makeWallSweepFromFaceBounds(
                sweep,
                sweepFaces,
                sweepWallGaps,
                rails,
                sz,
                profileSource,
                legacyRoofBehavior,
                effectiveMirror,
                entityHandleForSelectingReportingUnit,
                issues,
              ),
            );
          }

          break;
        }
        case WallType.SOLID: {
          /**
           * Solid Walls use the LOD1 layer for calculating their center of rotation.
           * @TODO - look into a unified positioning method for both solid and VOID walls that does not use
           * LOD1
           */
          let bottomOffset = 0;
          if (isRoofWall) {
            bottomOffset += legacyRoofBehavior ? roofOffsetZ + roofFacadeTrimOffsetZ : roofFacadeTrimOffsetZ;
          }

          const sweepZ = datum.resolveVerticalDatum({
            level,
            extraOffset: datumOffset + bottomOffset,
            wallSide: sweepSide,
          });

          const sweepLevelOffset = worldInchesTolevel(sweepZ, level.floorElevation);

          const wallMassLOD1Layer = solidWallLayerWithFallback(wall, wallLayer, WallLOD.L1);
          const wallMassLOD1Edge = isLeftSide ? wallMassLOD1Layer.leftEdge : wallMassLOD1Layer.rightEdge;
          const sweepCenterOfRotation = makeWallSweepCenterOfRotation(
            wallSweep,
            wallMassLOD1Edge,
            wallStripEdge,
            wallStripDomain,
            sweepZ,
            effectiveMirror,
          );

          /**
           * @TODO - how many cases actually need to filter this. There is a chance this is just a
           * by product of the confusing nature of the sweepCenterOfRoration calculation
           */
          if (sweepCenterOfRotation === null) {
            continue;
          }

          const [sx, sy, sz] = sweepCenterOfRotation;

          let sweepFaces: Face[] = [];
          if (wallStripDomain) {
            sweepFaces = wallClipFaceProvider.clipFaces(wallSweepPerimeterFaces, wall.id);
          } else {
            /**
             * @NOTE - this should always be the wallSweepPerimiterFaces, ragardless of domain.
             * However, this should be removed in later work in order to isolate baseline changes
             */
            sweepFaces = wallClipFaceProvider.clipFaces(
              isLeftSide ? [wallMassLOD1Layer.leftFace] : [wallMassLOD1Layer.rightFace],
              wallMassLOD1Layer.id,
            );
          }

          const isHorizontalFacadeSweep = Math.abs(rotation) < ANGLE_EPSILON;
          if (!isHorizontalFacadeSweep) {
            breps.push(
              ...makeWallSweepFromRayInFaces(
                sweep,
                sweepFaces,
                sweepWallGaps,
                rails,
                sx,
                sy,
                sz,
                profileSource,
                discardWallsBSP,
                roofSoffitDepth,
                legacyRoofBehavior,
                effectiveMirror,
                entityHandleForSelectingReportingUnit,
                issues,
              ),
            );
          } else if (
            /**
             * This check is necessary to maintain legacy horizontal facade sweep behavior.
             * Ideally every facade would run through the above makeWallSweepFromRayInFaces
             */
            wallSectionSideCanApplyHorizontalFacadeSweep(wall, wallLayer, wallLOD, sweepLevelOffset, isLeftSide)
          ) {
            /**
             * @NOTE - here we position faces redardless of whether they actually cross
             * the bounds of the sweep faces created earlier. Instead, we use the domain of those faces
             * and draw the sweep as if it crossed them, then move the sweep to the desired height
             */
            breps.push(
              ...makeWallSweepFromFaceBounds(
                sweep,
                sweepFaces,
                sweepWallGaps,
                rails,
                sz,
                profileSource,
                legacyRoofBehavior,
                effectiveMirror,
                entityHandleForSelectingReportingUnit,
                issues,
              ),
            );
          }
          break;
        }
        default: {
          assertUnreachable(wall);
        }
      }
    }
  }
}

function makeWallSweepFacadeWallGaps(
  wall: Wall,
  wallSweep: WallSweep,
  wallLayer: WallLayerType,
  isLeftSide: boolean,
): WallGap[] {
  /**
   * There are no gaps on VOID walls right now, but this may
   * change. If there are ever windows/gaps above/below a bay window, we will
   * need wallGaps in VOID walls.
   */
  if (wall.wallType !== WallType.SOLID) {
    return [];
  }

  /**
   * this is the layer that the gaps were positioned on in the solid wall.
   * It is independent of the wallSweep being created
   */
  const wallMassLOD1Layer = solidWallLayerWithFallback(wall, wallLayer, WallLOD.L1);

  /**
   * @TODO - this should be replaced with the true wall gap opening outlines
   * for sweep/gap intersections and then passed directly into
   * makeMaterialWallSweepOnPlane
   */
  return getWallGaps({
    leftEdge: wallMassLOD1Layer.leftEdge,
    rightEdge: wallMassLOD1Layer.rightEdge,
    leftSide: wall.leftSide,
    rightSide: wall.rightSide,
    includeExteriorCasing: wallSweep.includeOpeningFramesInGaps,
    isLeftSide,
    mergeGaps: true,
  });
}

function makeWallSweepFromRayInFaces(
  sweep: MaterialWallSweep,
  sweepDomainFaces: Face[],
  sweepWallGaps: WallGap[],
  rails: WallSweepRail[],
  sweepCenterX: WorldInches,
  sweepCenterY: WorldInches,
  sweepCenterZ: WorldInches,
  profileSource: ActiveProfileSource,
  discardWallsBSP: NodeBSP | null,
  roofSoffitDepth: number,
  legacyRoofBehavior: boolean,
  mirror: boolean,
  entityHandleForSelectingReportingUnit: DocEntityHandle | undefined,
  issues?: BuildingGenerationIssue[],
) {
  const {
    wallSweep: { rotation = 0, startOffset = 0, endOffset = 0, elevationNoteEnabled, elevationNote, profileId },
  } = sweep;

  const sweepResults: BRepViewEntity[] = [];

  const sweepProfileDescriptor = getActiveProfileDescriptor(
    profileSource,
    profileId,
    getDefaultProfileDescriptor(legacyRoofBehavior),
  );
  if (!sweepProfileDescriptor) {
    pushMissingProfileReferenceIssue(issues, profileSource, profileId, sweep.facadePickHandle, "profileId");
    return sweepResults;
  }
  const sweepProfile = getProfileExtrusionDescriptorProfile(sweepProfileDescriptor);
  const sweepProfileDescriptorWithOverrides = overrideDescriptorInfo(sweepProfileDescriptor, {
    elevationNoteEnabled,
    elevationNote,
  });

  /**
   * Create sweeps from the intersection of the rotated sweep
   * rail and the sweep domain faces.
   */
  const sweepRotation = adjustRotationToPreventVertical(rotation);
  for (const sweepDomainFace of sweepDomainFaces) {
    const srcSweepRailOnFacePlane = facadeSweepRail2FromRayInFace(
      sweepDomainFace,
      sweepCenterX,
      sweepCenterY,
      sweepCenterZ,
      sweepRotation,
      mirror,
    );

    /**
     * In the event that no intersection is found, a sweep cannot be
     * created at the face
     */
    if (!srcSweepRailOnFacePlane) {
      continue;
    }

    let sweepRailsOnFacePlane = [srcSweepRailOnFacePlane];

    // clip the sweep rails above the roof if necessary
    if (discardWallsBSP) {
      const removeSweepsAboveRoof: ClipFaceContext = {
        clippingNodes: [clippingNodeNew(discardWallsBSP, true)],
        epsilon: MULTIGABLE_BSP_EPSILON,
      };

      const refPlane = planeCopy(sweepDomainFace.plane);

      // we need to apply this offset to match the old roof facade trim behavior
      if (legacyRoofBehavior) {
        planeMove(refPlane, 0, 0, -roofSoffitDepth + 1);
      }

      sweepRailsOnFacePlane = clipRailOnPlane({
        rail: srcSweepRailOnFacePlane,
        plane: refPlane,
        options: removeSweepsAboveRoof,
      });

      if (sweepRailsOnFacePlane.length === 0) {
        sweepRailsOnFacePlane = [srcSweepRailOnFacePlane];
      }
    }

    // create a sweep for each rail
    for (let i = 0, l = sweepRailsOnFacePlane.length; i < l; i++) {
      const sweepRailOnFacePlane = sweepRailsOnFacePlane[i];

      applyExtensionsToFacadeSweepRail2(sweepRailOnFacePlane, i === 0 ? startOffset : 0, i === l - 1 ? endOffset : 0);

      assertIsNotNullOrUndefined(sweepProfileDescriptorWithOverrides);

      const { breps, clippedRails } = makeMaterialWallSweepOnPlane(
        sweep,
        sweepProfile,
        sweepDomainFace.plane,
        sweepRailOnFacePlane,
        sweepProfileDescriptorWithOverrides,
        sweepWallGaps,
        mirror,
        entityHandleForSelectingReportingUnit,
      );

      for (const clippedRail of clippedRails) {
        rails.push(
          wallSweepRailNew(
            polyLine3OnPlane(clippedRail, sweepDomainFace.plane),
            sweepProfileDescriptor.name,
            profileId,
            sweep.material,
          ),
        );
      }

      sweepResults.push(...breps);
    }
  }

  return sweepResults;
}

/**
 * @BEWARE - Here be hacks...
 *
 * @NOTE - This code path exists to maintain a slew of legacy features of strictly HORIZONTAL facade spans.
 * These spans bypass the usual ray in face check, and allows the positioning of
 * sweeps that are outside the domain of the wall.
 *
 * This escape hatch is often used for user hacks to make up for feature debt in
 * other parts of the system. Removing it should be done with caution.
 *
 * @TODO - Ultimately all facades should use the makeWallSweepFromRayInFaces code path
 */
function makeWallSweepFromFaceBounds(
  sweep: MaterialWallSweep,
  sweepDomainFaces: Face[],
  sweepWallGaps: WallGap[],
  rails: WallSweepRail[],
  sweepCenterZ: WorldInches,
  profileSource: ActiveProfileSource,
  legacyRoofBehavior: boolean,
  mirror: boolean,
  entityHandleForSelectingReportingUnit: DocEntityHandle | undefined,
  issues?: BuildingGenerationIssue[],
) {
  const {
    wallSweep: { startOffset = 0, endOffset = 0, elevationNoteEnabled, elevationNote, profileId },
  } = sweep;

  const sweepResults: BRepViewEntity[] = [];

  const sweepProfileDescriptor = getActiveProfileDescriptor(
    profileSource,
    profileId,
    getDefaultProfileDescriptor(legacyRoofBehavior),
  );
  if (!sweepProfileDescriptor) {
    pushMissingProfileReferenceIssue(issues, profileSource, profileId, sweep.facadePickHandle, "profileId");
    return sweepResults;
  }
  const sweepProfile = polyLine2SetCW(getProfileExtrusionDescriptorProfile(sweepProfileDescriptor));
  const sweepProfileDescriptorWithOverrides = overrideDescriptorInfo(sweepProfileDescriptor, {
    elevationNoteEnabled,
    elevationNote,
  });

  for (const sweepDomainFace of sweepDomainFaces) {
    const sweepRailOnFacePlane = facadeSweepRail2FromFaceBounds(sweepDomainFace, sweepCenterZ, mirror);

    applyExtensionsToFacadeSweepRail2(sweepRailOnFacePlane, startOffset, endOffset);
    assertIsNotNullOrUndefined(sweepProfileDescriptorWithOverrides);

    const { breps, clippedRails } = makeMaterialWallSweepOnPlane(
      sweep,
      sweepProfile,
      sweepDomainFace.plane,
      sweepRailOnFacePlane,
      sweepProfileDescriptorWithOverrides,
      sweepWallGaps,
      mirror,
      entityHandleForSelectingReportingUnit,
    );

    for (const clippedRail of clippedRails) {
      rails.push(
        wallSweepRailNew(
          polyLine3OnPlane(clippedRail, sweepDomainFace.plane),
          sweepProfileDescriptor.name,
          profileId,
          sweep.material,
        ),
      );
    }

    sweepResults.push(...breps);
  }

  return sweepResults;
}

/**
 *
 * Gets the relative position of the center of rotation along the wallMassEdge
 * in order to rotate the edge and generate sweeps
 *
 * This function is copied from various places that all mutate the center of
 * rotation for a given wall sweep, in an attempt to unify that logic into a single
 * call. Much of this could be revisited in the future, as wallStripDomain and other
 * parameters are on their way out
 */
function makeWallSweepCenterOfRotation(
  wallSweep: WallSweep,
  wallMassEdge: PolyLine2,
  wallStripEdge: PolyLine2 | null,
  wallStripDomain: boolean,
  wallSweepZ: WorldInches,
  mirror: boolean,
): [WorldInches, WorldInches, WorldInches] | null {
  const wallMassEdgeLength = polyLine2Length(wallMassEdge);
  const wallStripEdgeLength = wallStripEdge ? polyLine2Length(wallStripEdge) : 0;
  const wallSweepEdgeLength = wallStripDomain ? wallStripEdgeLength : wallMassEdgeLength;

  let { centerOfRotation, centerOfRotationNormalized } = wallSweep;
  centerOfRotation = centerOfRotation ?? 0.5;
  centerOfRotationNormalized = centerOfRotationNormalized ?? true;

  centerOfRotation = centerOfRotationNormalized ? centerOfRotation * wallSweepEdgeLength : centerOfRotation;
  centerOfRotation = centerOfRotation >= 0 ? centerOfRotation : wallSweepEdgeLength + centerOfRotation;

  // from here onward the centerOfRotation is denormalized

  // remap to wallStrip domain
  if (wallStripDomain && wallStripEdge?.length === 4) {
    const startParam = closestParamLine2(
      wallStripEdge[0],
      wallStripEdge[1],
      wallStripEdge[2],
      wallStripEdge[3],
      wallMassEdge[0],
      wallMassEdge[1],
    );
    const endParam = closestParamLine2(
      wallStripEdge[0],
      wallStripEdge[1],
      wallStripEdge[2],
      wallStripEdge[3],
      wallMassEdge[2],
      wallMassEdge[3],
    );

    if (centerOfRotation < startParam || centerOfRotation > endParam) {
      /**
       * This check seems to exist to ensure that the sweep is only generated once for
       * any sweep that exists on multiple edges.
       *
       * @TODO - check if this can be removed
       */
      return null;
    }

    centerOfRotation = centerOfRotation - startParam;
  }

  /**
   * Flips the center of rotation if it's less than 0.
   * This seems like an odd feature, and doesn't do the same thing
   * if the center of rotation is normalized and more than 1,
   * of not normalized and more than the edge length
   */
  if (centerOfRotation < 0) {
    centerOfRotation = wallMassEdgeLength + centerOfRotation;
  }

  const pl2 = polyLine2Copy(wallMassEdge);
  if (mirror) {
    polyLine2Reverse(pl2);
  }

  const [centerOfRotationX, centerOfRotationY] = polyLine2EdgeEval(pl2, 0, centerOfRotation, false);
  return [worldInchesNew(centerOfRotationX), worldInchesNew(centerOfRotationY), wallSweepZ];
}

/**
 * Creates a union of the left or right faces of a specific wall layer of a wall that
 * can have facades applied. These unioned faces are used for calculating facade sweep extents
 */
function wallSideFacadePerimeterFaces(
  wall: Wall,
  wallLayer: WallLayerType,
  wallLOD: WallLOD,
  leftSide: boolean,
  isRoofWall: boolean,
  roofEdges: Table<RoofWallSystemEdge> = {},
): Face[] {
  const faceLoops: PolyLine2[] = [];

  if (isRoofWall && wall.wallType === WallType.VOID) {
    const face = roofEdges[wall.id]?.referenceFace;
    return face ? [face] : [];
  }

  const wallMass = wall.wallType === WallType.SOLID ? wall.mass : wall;
  const wallLayers = wallMassLayers(wallMass, wallLayer, wallLOD)
    .filter(isSolidWallMass)
    .filter(({ section }) => {
      return wallSectionSideCanApplyFacade(getWallPath(wall).wallDescriptor, section, leftSide);
    });
  if (!wallLayers.length) {
    return [];
  }

  const faceLayer = wallLayers[0];
  const facePlane = planeCopy(leftSide ? faceLayer.leftFace.plane : faceLayer.rightFace.plane);
  // perimeter faces are relative to the wall positioning z in order to match up with openings/trim/etc...
  facePlane.originZ = wallMass.floorElevation + wallMass.section.bottomOffset;

  for (const layer of wallLayers) {
    // TODO - consider filtering faces here
    const face = leftSide ? layer.leftFace : layer.rightFace;
    const loop = planeReProjectPolyLine2(face.loops[0], face.plane, facePlane);
    faceLoops.push(loop);
  }

  const faceUnion = polygonUnion({ pls: faceLoops.map((p) => [p]) });
  return faceUnion.map((p) => faceNew(p, facePlane));
}

/**
 * Detects whether this wall can a apply a facade at any vertical domain on the wall. Generally, if a wall has exterior
 * cladding (i.e the wall assembly has exterior finish layers), a facade can be applied to that wall.
 * TODO - this will become more explicit with finish assemblies
 */
export function wallSideCanApplyFacade(wall: Wall, leftSide: boolean) {
  return wallSideHasCladdingType(wall, WallCladdingType.EXTERIOR, leftSide);
}

function wallSectionSideCanApplyHorizontalFacadeSweep(
  wall: Wall,
  wallLayer: WallLayerType,
  wallLOD: WallLOD,
  sweepLevelOffset: LevelInches,
  isLeftSide: boolean,
) {
  const sweepWallCrossSection = wallCrossSectionLayer(wall, wallLayer, wallLOD, sweepLevelOffset);
  if (!sweepWallCrossSection || sweepWallCrossSection.wallType === WallType.VOID) {
    // for legacy reasons we currently always allow horizontal sweeps outside the domain of the wall
    return true;
  }

  const { wallDescriptor } = getWallPath(wall);
  const { section: wallSection } = sweepWallCrossSection;

  const wallCut = wallSectionSideCutType(wallDescriptor, wallSection, isLeftSide);
  if (wallCut === WallSideCutType.FOUNDATION) {
    // for legacy reasons we currently always allow horizontal sweeps on foundation sections
    return true;
  }

  return wallSectionSideCanApplyFacade(wallDescriptor, wallSection, isLeftSide);
}

export function adjustRotationToPreventVertical(rotation: number) {
  //solves limitations in the lower level geometry api preventing 90 degree angles
  let resolvedRotation = rotation;
  if (resolvedRotation > 89.9 && resolvedRotation < 90.1) {
    resolvedRotation = 89.99;
  } else if (resolvedRotation > 269.9 && resolvedRotation < 270.1) {
    resolvedRotation = 269.99;
  }
  return resolvedRotation;
}

/**
 * Facades are typically applied to an interior room edge, with the intention of rendering on the opposite planar
 * graph edge (usually a boundary edge with no room association). In the event that the facade uses a "wallStripDomain",
 * the facade needs to be placed along the entirety of the room edge regardless of sub planar graph edges.
 *
 * This function looks up the appropriate wallStrip for room edge calculation. This is tricky because interior
 * rooms that are adjacent to exterior rooms have two wall strips, one for the interior room and one for the exterior.
 *
 * In the event that the facade has two strips, this function determines which one to use.
 *
 * @param leftSide the intended side for the facade to use. in the event that both sides can
 * apply a facade, this is the applicable one to use
 */
function getFacadeWallStripRoom(wall: Wall, leftSide: boolean): Room | null {
  let wallRoom: Room | undefined;

  const path = getWallPath(wall);
  const { leftRoom, rightRoom } = path;
  if (!leftRoom) {
    // no right room - strip to left side
    wallRoom = rightRoom;
  } else if (!rightRoom) {
    // no left room  - strip to right side
    wallRoom = leftRoom;
  } else {
    // there are two rooms, we need to pick which side to take the baseline from
    const leftRoomCanApplyFacades = wallRoomCanApplyFacade(path.wallDescriptor, true);
    const rightRoomCanApplyFacades = wallRoomCanApplyFacade(path.wallDescriptor, false);

    // no facades - should have been caught in earlier checks but this is for completeness
    if (!leftRoomCanApplyFacades && !rightRoomCanApplyFacades) {
      return null;
    }
    // both facades - use the specified leftSide
    if (leftRoomCanApplyFacades && rightRoomCanApplyFacades) {
      wallRoom = leftSide ? leftRoom : rightRoom;
    }

    // only one side applicable - baseline to side that cannot apply facade.
    // In this case, the facade will likely have been applied to the "outside" of an interior room.
    // the baseline specification should be the interior room edge and not the exterior room edge.
    wallRoom = leftRoomCanApplyFacades ? rightRoom : leftRoom;
  }

  // catch unhandled case where there is no room - this should not happen
  if (!wallRoom?.id) {
    return null;
  }

  return wallRoom;
}

/**
 * Detect the full baseline of a room edge for facades using a wallStripDomain
 */
function facadeWallSweepBaseline(
  wallIndex: WallIndex,
  wall: Wall,
  wallLayer: WallLayerType,
  wallLOD: WallLOD,
  leftSide: boolean,
  cutPlane?: LevelInches,
): PolyLine2 | null {
  const wallStripRoom = getFacadeWallStripRoom(wall, leftSide);
  if (!wallStripRoom) {
    return null;
  }

  const wallStrip = wallIndex.parentWallStrip(wallStripRoom, wall);
  if (!wallStrip) {
    return null;
  }

  const walls = wallStrip.toWallArray();
  const wallStripWalls = walls.filter(wallIsSolid);
  if (wallStripWalls.length === 0) {
    return null;
  }

  const edgeArr: number[] = [];

  const wallPath = getWallPath(wall);
  for (const wallStripWall of wallStripWalls) {
    const wallStripRoomAligned = getWallPath(wallStripWall).leftRoom === wallPath.leftRoom;
    const wallStripRoomLeftSide = wallStripRoomAligned ? leftSide : !leftSide;
    const wallStripWallLayer = solidWallLayerWithFallback(wallStripWall, wallLayer, wallLOD, cutPlane);

    const wallStripRoomEdge = wallStripRoomLeftSide ? wallStripWallLayer.leftEdge : wallStripWallLayer.rightEdge;
    edgeArr.push(...wallStripRoomEdge);
  }

  return polyLine2Simplify(polyLine2New(edgeArr));
}

const THICKEN_OFFSET_FOR_OPENING_INTERSECTION_POLYGON = 0.001;
const THICKEN_OFFSET_ADDITIONAL_LENGTH_FOR_CULLING = THICKEN_OFFSET_FOR_OPENING_INTERSECTION_POLYGON + 0.0001;
const EXTENSION_LENGTH_FOR_COLLINEAR_TEST_LINE = 1000;

export function makeMaterialWallSweepOnPlane(
  sweep: MaterialWallSweep,
  sweepProfile: PolyLine2,
  sweepPlane: Plane,
  sweepRail2OnPlane: PolyLine2,
  sweepDescriptor: BaseDescriptor,
  sweepGaps: WallGap[],
  mirror: boolean,
  entityHandleForSelectingReportingUnit: DocEntityHandle | undefined,
) {
  const facadeSweepResult: BRepViewEntity[] = [];

  const { wallSweep, facadePickHandle, material, materialTransformation } = sweep;
  const { trimmedByOpenings, dropIntervalsLessThan, surfaceOffset = 0, useLegacyWallGapBounds } = wallSweep;

  sweepProfile = polyLine2MergeCoincidentVertices(sweepProfile);

  // extrudeFaceAlongPolyLine3 orients its end-cap faces from the sweep profile
  // winding: a CCW profile yields caps wound opposite to the Face convention (outer loop CW),
  // which results in degenerate geometry.
  const profileNeedsRepair = polyLine2WindingDir(sweepProfile) === WindingDir.CCW;

  if (mirror) {
    /**
     * The sweep face profile is relative to the outside, so in the event that
     * We mirrored this rail, it will not match up with the
     * "outside" normal expected by the profile.
     *
     * In order to maintain it's position
     * relative to the face sweep profile, we need to reverse the rail
     */
    sweepRail2OnPlane = polyLine2ReverseCopy(sweepRail2OnPlane);
  }

  const [snx, sny, snz] = planeNormal(sweepPlane);

  const openingRects: PolyLine2[] = [];
  if (useLegacyWallGapBounds) {
    /**
     * Preserve legacy sweep bounds behavior; the useLegacy- flag can be unset by the
     * user but can never set to be true.
     *
     * @TODO: Once no production homes contain this flag, remove this fork in the code.
     */
    for (const { start, end, bottom, height } of sweepGaps) {
      openingRects.push(rect2ByBottomLeftWidthHeight(start, bottom, end - start, height));
    }
  } else {
    for (const { openings } of sweepGaps) {
      for (const wallOpening of openings) {
        const { opening } = wallOpening;
        if (!opening) {
          continue;
        }
        let side: OpeningSideName = "FRONT";
        let oppSide = sideOpposite(side);

        // select the side of the opening that is facing the same way as the sweep
        const contextPlane = wallOpeningSide(opening, side).context?.openingPlane;
        if (contextPlane) {
          const [onx, ony, onz] = planeNormal(contextPlane);
          if (vec3AngleBetween(snx, sny, snz, onx, ony, onz) > ANGLE_EPSILON) {
            [side, oppSide] = [oppSide, side];
          }
        }

        openingRects.push(wallOpeningOpening2(opening, side, true));
      }
    }
  }

  const segments: PolyLine2[] = [];

  if (openingRects.length === 0 || !trimmedByOpenings) {
    segments.push(polyLine2Copy(sweepRail2OnPlane));
  } else {
    //thicken polyline to enable using polygonClip lib to trim with multiple polygons
    const trimmedPolys = polygonSubtract([
      [
        polyLine2Thicken(polyLine2Copy(sweepRail2OnPlane), THICKEN_OFFSET_FOR_OPENING_INTERSECTION_POLYGON),
        ...openingRects,
      ],
    ]);
    //extend line so that all possible collinear edges are captured
    const collinearTestLine = polyLine2Extend(
      polyLine2Copy(sweepRail2OnPlane),
      EXTENSION_LENGTH_FOR_COLLINEAR_TEST_LINE,
      EXTENSION_LENGTH_FOR_COLLINEAR_TEST_LINE,
    );

    const [testTangentX, testTangentY] = polyLine2EdgeTangent(collinearTestLine, 0);

    for (const trimmedPoly of trimmedPolys) {
      //there shouldn't be holes so only need to grab the outline polyline
      const pl = trimmedPoly[0];
      const edgeCount = polyLine2EdgeCount(pl);

      for (let i = 0, l = edgeCount; i < l; i++) {
        const edgeLength = polyLine2EdgeLength(pl, i);
        //short edges will register as collinear, so remove them since we intentionally created them during the thicken process
        if (
          edgeLength < THICKEN_OFFSET_ADDITIONAL_LENGTH_FOR_CULLING ||
          (dropIntervalsLessThan && edgeLength < dropIntervalsLessThan)
        ) {
          continue;
        }
        const edge = polyLine2Edge(pl, i);
        const [edgeTangentX, edgeTangentY] = polyLine2EdgeTangent(edge, 0);

        //only grab edges collinear to the originally-thickened line and pointing in the same direction
        if (
          vec2Dot(testTangentX, testTangentY, edgeTangentX, edgeTangentY) > ONE_MINUS_DIST_EPSILON &&
          line2Collinear(
            collinearTestLine[0],
            collinearTestLine[1],
            collinearTestLine[2],
            collinearTestLine[3],
            edge[0],
            edge[1],
            edge[2],
            edge[3],
          )
        ) {
          segments.push(polyLine2Edge(pl, i));
        }
      }
    }
  }

  const [planeNormalX, planeNormalY] = planeNormal(sweepPlane);

  for (const segment of segments) {
    const res = polyLine2OnPlane(sweepPlane, segment);
    const brep = extrudeFaceAlongPolyLine3(
      [sweepProfile],
      polyLine3Move(polyLine3Copy(res), planeNormalX * surfaceOffset, planeNormalY * surfaceOffset, 0),
      0,
      0,
      1,
    );
    if (brep) {
      if (profileNeedsRepair) {
        brep.faces.forEach(faceRepairLoopDirections);
      }
      const sweepMaterialTransformation = materialTransformation
        ? { ...materialTransformation, isMirrored: mirror }
        : undefined;
      facadeSweepResult.push(
        brepViewEntityNew(
          brep,
          entityHandleForSelectingReportingUnit,
          sweepDescriptor,
          material,
          facadePickHandle,
          sweepMaterialTransformation,
        ),
      );
    } else {
      console.error(`trim profile ${sweepDescriptor.elevationNote} cannot be extruded along that line`);
    }
  }

  return { breps: facadeSweepResult, clippedRails: segments };
}

function getDefaultProfileDescriptor(legacyRoofBehavior: boolean) {
  return legacyRoofBehavior ? DEFAULT_TRIM_PROFILE_DESCRIPTOR : DEFAULT_PROFILE_DESCRIPTOR;
}

function legacyWallSweepPerimeterFaces(wallSweepPerimeterFaces: Face[], roofEdge: RoofWallSystemEdge) {
  if (!roofEdge) {
    return wallSweepPerimeterFaces;
  }

  const { referenceEdge } = roofEdge;

  if (!referenceEdge) {
    return wallSweepPerimeterFaces;
  }

  const [x0, y0] = polyLine2Start(referenceEdge);
  const [x1, y1] = polyLine2End(referenceEdge);
  const [tx, ty] = polyLine2EdgeTangent(referenceEdge, 0);

  const startPlane = planeBSPByPointAndNormal(x0, y0, 0, -tx, -ty, 0);
  const endPlane = planeBSPByPointAndNormal(x1, y1, 0, tx, ty, 0);
  const edgeBSP = addConvexPlanesToBSPLeaf([startPlane, endPlane]);

  const facePlane = planeCopy(wallSweepPerimeterFaces[0].plane);
  const clippedFaces = clipFaces({
    faces: wallSweepPerimeterFaces,
    options: {
      clippingNodes: [clippingNodeNew(edgeBSP, true)],
      epsilon: MULTIGABLE_BSP_EPSILON,
    },
  });

  const faceLoops: PolyLine2[] = [];
  for (const clippedFace of clippedFaces) {
    const loop = planeReProjectPolyLine2(clippedFace.loops[0], clippedFace.plane, facePlane);
    faceLoops.push(loop);
  }

  const faceUnion = polygonUnion({ pls: faceLoops.map((p) => [p]) });
  return faceUnion.map((p) => faceNew(p, facePlane));
}
